import React, { useEffect } from 'react';

import {
  Flex,
  PresentationalCheckboxGroup,
  PresentationalRadioGroup,
  Input,
  Box,
} from '@opendoor/bricks/core';

import { HoaFeeInput } from 'sellDirect/components/closingDash/contract/HoaFeeInput';
import { YES, NO } from 'sellDirect/components/closingDash/form/FormRadioInput';

import { HOAFee } from 'sellDirect/declarations/HOAFee';
import { HoaAmenitiesOptions } from 'sellDirect/declarations/Amenities';

import { FeeFrequency } from 'sellDirect/helpers/closingDash/constants';
import { IResetableFormRenderProps } from 'sellDirect/components/closingDash/ResetableForm';
import { CustomInputLabel } from 'sellDirect/components/closingDash/form/CustomInputLabel';

interface HoaDetailsFormProps {
  shouldSeeAgentExperience: boolean;
  resetableForm: IResetableFormRenderProps;
  disabled: boolean;
}
const HAS_HOA_ID = 'hasHoa';
const HOA_AMENITIES_ID = 'hoaAmenities';
const HOANameId = 'hoaName';
/**
 * HOA details form for the SignContract flow
 *
 * component-test-check-ignore
 * storybook-check-ignore
 */
export const HoaDetailsForm: React.FC<HoaDetailsFormProps> = ({
  shouldSeeAgentExperience,
  disabled,
  resetableForm: { formState, onUpdate, onUpdateMany },
}) => {
  const { hasHoa, hoaAmenities, hoaName, hoaFees } = formState;
  const pendingHoaFees = hoaFees?.length ? hoaFees : [{}];
  const updateHoaFeeAmount = (index: number, amount: number) => {
    const newFees = hoaFees?.length
      ? hoaFees.map((fee: HOAFee, i: number) => {
          return i === index ? { ...fee, amount } : fee;
        })
      : [{ amount }];

    onUpdate('hoaFees', newFees);
  };

  const updateHoaFeeFrequency = (index: number, frequency: FeeFrequency) => {
    const newFees = hoaFees?.length
      ? hoaFees.map((fee: HOAFee, i: number) => {
          return i === index ? { ...fee, frequency } : fee;
        })
      : [{ frequency }];

    onUpdate('hoaFees', newFees);
  };

  useEffect(() => {
    if (hasHoa === false) {
      // if no HOA, deselect all HOA details
      onUpdateMany([
        {
          id: 'hoaFees',
          value: null,
        },
        {
          id: 'hoaName',
          value: null,
        },
        {
          id: 'hoaAmenities',
          value: {
            clubhouse: null,
            dogPark: null,
            golf: null,
            gym: null,
            other: null,
            otherInfo: null,
            park: null,
            pool: null,
            sauna: null,
            sportsCourts: null,
            tennisCourt: null,
            trails: null,
            trashRecycling: null,
          },
        },
      ]);
    }
    // eslint-disable-next-line
  }, [hasHoa]);
  return (
    <Flex data-testid="hoa-details-container" flexDirection="column" gridGap={3}>
      <Box as="fieldset" border="unset" padding="0" margin="0">
        <CustomInputLabel
          title={`Is ${shouldSeeAgentExperience ? 'the' : 'your'} home part of an HOA?`}
          id={HAS_HOA_ID}
          required={true}
          titleFontSize="s1"
          isLegend
          titleFontWeight="semibold"
        />
        <PresentationalRadioGroup
          analyticsName="cake-closing-contract-has-hoa"
          name="hasHOA"
          onClick={(hasHoa: string) => onUpdate('hasHoa', hasHoa === YES)}
          selectedValue={hasHoa != null ? getHasHoaStringValue(hasHoa) : undefined}
          disabled={disabled}
          options={[
            {
              label: YES,
              value: YES,
            },
            {
              label: NO,
              value: NO,
            },
          ]}
        />
      </Box>
      {hasHoa && (
        <Flex flexDir="column" gridGap="8">
          <Input
            analyticsName="cake-closing-dash-hoa-details-hoa-name"
            type="text"
            id={HOANameId}
            data-testid={HOANameId}
            value={hoaName}
            onChange={(_event, value) => onUpdate('hoaName', value)}
            unboundedWidth
            disabled={disabled}
            label="HOA name"
            required
          />
          {pendingHoaFees?.map((fee: HOAFee, index: number) => (
            <HoaFeeInput
              key={`hoaFeeInput${index}`}
              index={index}
              updateHoaFeeAmount={updateHoaFeeAmount}
              updateHoaFeeFrequency={updateHoaFeeFrequency}
              fee={fee}
              disabled={disabled}
            />
          ))}
          <Flex
            as="fieldset"
            flexDirection="column"
            border="unset"
            padding="0"
            margin="0"
            gridGap="8"
          >
            <CustomInputLabel
              title={`What amenities are offered by ${
                shouldSeeAgentExperience ? 'the' : 'your'
              } HOA or community?`}
              subtitle="(Select all that apply)"
              id={HOA_AMENITIES_ID}
              titleFontSize="s1"
              isLegend
              titleFontWeight="semibold"
              required={true}
            />
            <PresentationalCheckboxGroup
              analyticsName="cake-hoa-amentities"
              name="hoaAmenities"
              onClick={(value) => {
                onUpdate('hoaAmenities', { ...hoaAmenities, [value]: !hoaAmenities[value] });
              }}
              options={HoaAmenitiesOptions}
              selectedValues={getHoaSelectedValues(hoaAmenities)}
              disabled={false}
              data-testid="hoa-options"
            />
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};

/** Given a boolean, returns the string value for the hasHoa checkbox */
const getHasHoaStringValue = (hasHoa: boolean): string => (hasHoa ? YES : NO);
/** Helper function to get the selected fields in the HOA amenities menu*/
const getHoaSelectedValues = (amenities: { [s: string]: boolean | null }): Array<string> => {
  const selectedValues = [];
  // the amenities object will have null fields for those that are not selected
  for (const [k, v] of Object.entries(amenities)) {
    if (v) {
      selectedValues.push(k);
    }
  }
  return selectedValues;
};
